/**
 * Copyright SimVentions, Inc. Usage, distribution, transferal, and licensing
 * of this source code is protected under SBIR law as described in DFARS 252.227-7018.
 *
 * SBIR data rights fully described in the README.md file in the top level directory of this project.
 */
import * as React from "react";
import { FileType } from "Api";
import {
  FaDownload,
  FaFolder,
  FaQuestionCircle,
  FaTrash,
} from "react-icons/fa";
import { ThemeNames, ThemeType } from "../DesignSystem/Theme/Theme";
import { QuickFormFile, UploadStatus } from "./QuickFormTypes";
import { TransformStatusIcon } from "./QuickFormStatusIndicator";
import { QuickFormValidationIcon } from "./QuickFormValidationIcon";
import { PLACEMENT, StatefulTooltip } from "baseui/tooltip";
import { getTooltipOverrides } from "../DesignSystem/TooltipStyles";
import { useDexterTheme } from "../DesignSystem/Theme/ThemeContext";

const PROTO_LOGO_URL = new URL(
  "../resources/images/protobuf_logo.png",
  import.meta.url
);

const XML_LOGO_URL = new URL(
  "../resources/images/xml_logo.svg",
  import.meta.url
);

const XML_LOGO_DARK_URL = new URL(
  "../resources/images/xml_logo_dark.svg",
  import.meta.url
);

interface QuickFormTableProps {
  theme: ThemeType;
  files: QuickFormFile[];
  allSelected: boolean;
  selectedCount: number;
  onToggleAll: () => void;
  onToggleFile: (fileId: string) => void;
  onDetectedTypeChange: (fileId: string, type: FileType) => void;
  onTransformToChange: (fileId: string, type: FileType | null) => void;
  onRemoveFile: (fileId: string) => void;
  onDownloadFile: (fileId: string) => void;
  onCancelUpload: (fileId: string) => void;
  onShowErrorDetails: (fileId: string, errorMessage: string) => void;
  onBulkSetTransformType: (type: FileType) => void;
  onBulkSetDetectedType: (type: FileType) => void;
}

const spinnerKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

export const QuickFormTable: React.FC<QuickFormTableProps> = ({
  theme,
  files,
  allSelected,
  selectedCount,
  onToggleAll,
  onToggleFile,
  onDetectedTypeChange,
  onTransformToChange,
  onRemoveFile,
  onDownloadFile,
  onCancelUpload,
  onShowErrorDetails,
  onBulkSetTransformType,
  onBulkSetDetectedType,
}) => {
  const [bulkTransformType, setBulkTransformType] = React.useState<string>("");
  const [bulkDetectedType, setBulkDetectedType] = React.useState<string>("");

  const { currentTheme, currentThemeName } = useDexterTheme();
  const isCurrentThemeDark =
    currentThemeName == ThemeNames.COLORBLIND_DARK ||
    currentThemeName == ThemeNames.DARK;

  const getFileIcon = (type: FileType | null): JSX.Element => {
    if (!type || type === FileType.UNDETECTED) {
      return (
        <FaQuestionCircle
          size={18}
          style={{
            color: theme.colors.secondary,
            verticalAlign: "middle",
          }}
          title="Unknown file type"
        />
      );
    }

    switch (type) {
      case FileType.XML:
        return (
          <img
            src={
              isCurrentThemeDark
                ? XML_LOGO_DARK_URL.toString()
                : XML_LOGO_URL.toString()
            }
            alt="XML"
            style={{
              width: "30px",
              height: "30px",
              verticalAlign: "middle",
            }}
          />
        );
      case FileType.PROTO:
        return (
          <img
            src={PROTO_LOGO_URL.toString()}
            alt="Protobuf"
            style={{
              width: "18px",
              height: "18px",
              verticalAlign: "middle",
            }}
          />
        );
      default:
        return (
          <FaQuestionCircle
            size={18}
            style={{
              color: theme.colors.secondary,
              verticalAlign: "middle",
            }}
            title="Unknown file type"
          />
        );
    }
  };

  const formatFileSize = (sizeMbs?: number): string => {
    if (!sizeMbs) return "Unknown size";
    if (sizeMbs < 0.001) return `${(sizeMbs * 1024).toFixed(1)} KB`;
    return `${sizeMbs.toFixed(1)} MB`;
  };

  const formatTimeAgo = (uploadDateUtc?: string): string => {
    if (!uploadDateUtc) return "Unknown time";

    const uploadDate = new Date(uploadDateUtc);
    const now = new Date();
    const diffMs = now.getTime() - uploadDate.getTime();
    const diffMins = Math.floor(diffMs / 60000);

    if (diffMins < 1) return "just now";
    if (diffMins < 60) return `${diffMins} min ago`;
    const diffHours = Math.floor(diffMins / 60);
    if (diffHours < 24)
      return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  };

  const handleBulkTransformChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const selectedValue = e.target.value;
    if (selectedValue) {
      // Convert string to actual FileType enum value
      const selectedType = FileType[selectedValue as keyof typeof FileType];
      setBulkTransformType(selectedValue);
      onBulkSetTransformType(selectedType); // Pass the enum value, not the string

      // Reset after a brief moment
      setTimeout(() => setBulkTransformType(""), 100);
    }
  };

  const handleBulkDetectedChange = (
    e: React.ChangeEvent<HTMLSelectElement>
  ): void => {
    const selectedValue = e.target.value;
    if (selectedValue) {
      // Convert string to FileType enum
      const selectedType = FileType[selectedValue as keyof typeof FileType];
      setBulkDetectedType(selectedValue);
      onBulkSetDetectedType(selectedType);

      // Reset after a brief moment
      setTimeout(() => setBulkDetectedType(""), 100);
    }
  };

  return (
    <>
      <style>{spinnerKeyframes}</style>
      <div
        style={{
          paddingBottom: theme.spacing.xxl,
          overflowX: "auto",
        }}
      >
        <table
          style={{
            width: "100%",
            borderCollapse: "collapse",
            fontSize: theme.fontSizes.medium,
            fontFamily: theme.fonts.primary,
          }}
        >
          <thead>
            <tr
              style={{
                background: theme.colors.tableBackgroundColor,
                borderBottom: `2px solid ${theme.colors.searchMainColor}`,
              }}
            >
              <th
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  paddingLeft: "30px",
                  textAlign: "center",
                  fontWeight: 600,
                  color: theme.colors.text,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderRight: `1px solid ${theme.colors.searchMainColor}`,
                  background: theme.colors.tableBackgroundColor,
                  width: "40px",
                }}
              >
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={onToggleAll}
                  style={{
                    width: "18px",
                    height: "18px",
                    cursor: "pointer",
                    accentColor: theme.colors.primaryButtonColor,
                  }}
                />
              </th>
              <th
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  textAlign: "left",
                  fontWeight: 600,
                  color: theme.colors.text,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  maxWidth: "50px",
                  borderRight: `1px solid ${theme.colors.searchMainColor}`,
                  background: theme.colors.tableBackgroundColor,
                }}
              >
                File Name
              </th>
              <th
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  textAlign: "left",
                  fontWeight: 600,
                  color: theme.colors.text,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderRight: `1px solid ${theme.colors.searchMainColor}`,
                  background: theme.colors.tableBackgroundColor,
                }}
              >
                Detected Type
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: theme.spacing.md,
                  }}
                >
                  {selectedCount > 0 && (
                    <>
                      {/* Set Transform Type */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: theme.spacing.md,
                        }}
                      >
                        <select
                          data-testId="bulkDetectSelect"
                          value={bulkDetectedType}
                          onChange={handleBulkDetectedChange}
                          style={{
                            padding: "6px 32px 6px 10px",
                            border: `1px solid ${theme.colors.dropdownBorderColor}`,
                            borderRadius: "6px",
                            fontSize: "13px",
                            background: theme.colors.backgroundTertiary,
                            color: theme.colors.text,
                            cursor: "pointer",
                            fontFamily: theme.fonts.primary,
                            minWidth: "140px",
                          }}
                        >
                          <option value="" hidden>
                            Change file type for selected files
                          </option>
                          <option value={FileType.XML}>XML</option>
                          <option value={FileType.PROTO}>Protobuf</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </th>
              <th
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  textAlign: "left",
                  fontWeight: 600,
                  color: theme.colors.text,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderRight: `1px solid ${theme.colors.searchMainColor}`,
                  background: theme.colors.tableBackgroundColor,
                }}
              >
                Transform To
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {selectedCount > 0 && (
                    <>
                      {/* Set Transform Type */}
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                        }}
                      >
                        <select
                          data-testId="bulkTypeSelect"
                          value={bulkTransformType}
                          onChange={handleBulkTransformChange}
                          style={{
                            padding: "6px 32px 6px 10px",
                            border: `1px solid ${theme.colors.dropdownBorderColor}`,
                            borderRadius: "6px",
                            fontSize: "13px",
                            background: theme.colors.backgroundTertiary,
                            color: theme.colors.text,
                            cursor: "pointer",
                            fontFamily: theme.fonts.primary,
                            minWidth: "140px",
                          }}
                        >
                          <option value="" hidden>
                            Change transform type for selected files
                          </option>
                          <option value={FileType.XML}>XML</option>
                          <option value={FileType.PROTO}>Protobuf</option>
                          <option value={FileType.CPP}>C++</option>
                        </select>
                      </div>
                    </>
                  )}
                </div>
              </th>
              <th
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  textAlign: "left",
                  fontWeight: 600,
                  color: theme.colors.text,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  borderRight: `1px solid ${theme.colors.searchMainColor}`,
                  background: theme.colors.tableBackgroundColor,
                }}
              >
                Transform Status
              </th>
              <th
                style={{
                  padding: `${theme.spacing.md} ${theme.spacing.lg}`,
                  paddingRight: "30px",
                  textAlign: "left",
                  fontWeight: 600,
                  color: theme.colors.text,
                  fontSize: "13px",
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  background: theme.colors.tableBackgroundColor,
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {files.map((file) => {
              const isUploading = file.uploadStatus === UploadStatus.UPLOADING;
              const isDetecting =
                file.uploadStatus === UploadStatus.COMPLETE &&
                (!file.detectedType ||
                  file.detectedType === FileType.UNDETECTED) &&
                file.uploadProgress === 100;

              // Show question mark only when detection failed, not during upload/detection
              const shouldShowQuestionMark =
                !isUploading &&
                !isDetecting &&
                (!file.detectedType ||
                  file.detectedType === FileType.UNDETECTED);

              const icon = shouldShowQuestionMark
                ? getFileIcon(null)
                : getFileIcon(file.detectedType);

              return (
                <tr
                  key={file.id}
                  style={{
                    background: file.selected
                      ? theme.colors.fileNodeSelectedBackgroundColor
                      : theme.colors.tableBackgroundColor,
                    borderBottom: `1px solid ${theme.colors.searchMainColor}`,
                    transition: "background 0.2s",
                  }}
                  onMouseEnter={(e) => {
                    if (!file.selected) {
                      e.currentTarget.style.background =
                        theme.colors.backgroundTertiary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!file.selected) {
                      e.currentTarget.style.background =
                        theme.colors.tableBackgroundColor;
                    }
                  }}
                >
                  {/* Checkbox */}
                  <td
                    style={{
                      padding: theme.spacing.lg,
                      paddingLeft: "30px",
                      textAlign: "center",
                      verticalAlign: "middle",
                      borderRight: `1px solid ${theme.colors.searchMainColor}`,
                      borderTop: `1px solid ${theme.colors.searchMainColor}`,
                      cursor:
                        isUploading || isDetecting ? "not-allowed" : "pointer",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={file.selected}
                      onChange={() => onToggleFile(file.id)}
                      disabled={isUploading || isDetecting}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor:
                          isUploading || isDetecting
                            ? "not-allowed"
                            : "pointer",
                        accentColor: theme.colors.primaryButtonColor,
                      }}
                    />
                  </td>

                  {/* File Name with upload progress */}
                  <td
                    style={{
                      padding: theme.spacing.lg,
                      verticalAlign: "middle",
                      borderRight: `1px solid ${theme.colors.searchMainColor}`,
                      borderTop: `1px solid ${theme.colors.searchMainColor}`,
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: theme.spacing.md,
                      }}
                    >
                      <div
                        style={{
                          width: "36px",
                          height: "36px",
                          borderRadius: "6px",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          flexShrink: 0,
                          marginTop: "2px",
                          background: theme.colors.backgroundTertiary,
                        }}
                      >
                        {/* Show spinner during upload/detection */}
                        {isUploading || isDetecting ? (
                          <div
                            style={{
                              width: "18px",
                              height: "18px",
                              border: `2px solid ${theme.colors.dropdownBorderColor}`,
                              borderTop: `2px solid ${theme.colors.searchMainColor}`,
                              borderRadius: "50%",
                              animation: "spin 1s linear infinite",
                            }}
                          />
                        ) : (
                          icon
                        )}
                      </div>
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div
                          style={{
                            fontWeight: 500,
                            color: theme.colors.linkColor,
                            marginBottom: "2px",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            whiteSpace: "nowrap",
                            cursor: "pointer",
                          }}
                          title={file.fileInfo.attributes?.name}
                        >
                          {file.fileInfo.attributes?.name || "Unknown file"}
                        </div>

                        {/* Status area - 3 tier layout: file title, status, metadata */}
                        <div
                          style={{
                            height: "36px", // Fixed height prevents layout shift
                            display: "flex",
                            flexDirection: "column",
                            justifyContent: "flex-start",
                            gap: "2px",
                          }}
                        >
                          {/* Status indicator - middle tier */}
                          <div style={{ minHeight: "14px" }}>
                            {isUploading && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "space-between",
                                  gap: "6px",
                                }}
                              >
                                <div
                                  style={{
                                    display: "flex",
                                    alignItems: "center",
                                    gap: "6px",
                                  }}
                                >
                                  <div
                                    style={{
                                      width: "6px",
                                      height: "6px",
                                      borderRadius: "50%",
                                      background: theme.colors.secondary,
                                    }}
                                  />
                                  <span
                                    style={{
                                      fontSize: "11px",
                                      color: theme.colors.secondary,
                                      fontWeight: 500,
                                    }}
                                  >
                                    Uploading {file.uploadProgress}%
                                  </span>
                                </div>
                                <button
                                  onClick={() => onCancelUpload(file.id)}
                                  style={{
                                    fontSize: "11px",
                                    color: theme.colors.negative,
                                    fontWeight: 500,
                                    cursor: "pointer",
                                    background: "none",
                                    border: "none",
                                    padding: 0,
                                    textDecoration: "underline",
                                    fontFamily: theme.fonts.primary,
                                  }}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}

                            {isDetecting && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "6px",
                                    height: "6px",
                                    borderRadius: "50%",
                                    background: theme.colors.secondary,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: theme.colors.secondary,
                                    fontWeight: 500,
                                    fontStyle: "italic",
                                  }}
                                >
                                  Detecting...
                                </span>
                              </div>
                            )}

                            {!isUploading && !isDetecting && (
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  gap: "6px",
                                }}
                              >
                                <div
                                  style={{
                                    width: "6px",
                                    height: "6px",
                                    borderRadius: "50%",
                                    background: theme.colors.positive,
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: "11px",
                                    color: theme.colors.positive,
                                    fontWeight: 500,
                                  }}
                                >
                                  Ready
                                </span>
                              </div>
                            )}
                          </div>

                          {/* File metadata - bottom tier, always shown */}
                          <div
                            style={{
                              fontSize: "12px",
                              color: theme.colors.secondaryTextColor,
                            }}
                          >
                            {formatFileSize(file.fileInfo.attributes?.sizeMbs)}{" "}
                            ·{" "}
                            {formatTimeAgo(
                              file.fileInfo.attributes?.uploadDateUtc
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </td>

                  {/* Detected Type dropdown */}
                  <td
                    style={{
                      padding: theme.spacing.lg,
                      verticalAlign: "middle",
                      borderRight: `1px solid ${theme.colors.searchMainColor}`,
                      borderTop: `1px solid ${theme.colors.searchMainColor}`,
                    }}
                  >
                    {isUploading || isDetecting ? (
                      <span
                        style={{
                          fontSize: "12px",
                          color: theme.colors.secondaryTextColor,
                          fontStyle: "italic",
                        }}
                      >
                        {isUploading ? "Uploading..." : "Detecting..."}
                      </span>
                    ) : (
                      <select
                        value={file.detectedType || ""}
                        onChange={(e) =>
                          onDetectedTypeChange(
                            file.id,
                            e.target.value as FileType
                          )
                        }
                        style={{
                          padding: "6px 32px 6px 10px",
                          border: `1px solid ${theme.colors.dropdownBorderColor}`,
                          borderRadius: "6px",
                          fontSize: "13px",
                          background: theme.colors.backgroundTertiary,
                          color: theme.colors.text,
                          cursor: "pointer",
                          fontFamily: theme.fonts.primary,
                          minWidth: "110px",
                        }}
                      >
                        <option value={FileType.XML}>XML</option>
                        <option value={FileType.PROTO}>Protobuf</option>
                      </select>
                    )}
                  </td>

                  {/* Transform To dropdown */}
                  <td
                    style={{
                      padding: theme.spacing.lg,
                      verticalAlign: "middle",
                      borderRight: `1px solid ${theme.colors.searchMainColor}`,
                      borderTop: `1px solid ${theme.colors.searchMainColor}`,
                      backgroundColor:
                        file.validationResult?.isValid === false
                          ? theme.colors.validationWarningBackground
                          : "transparent",
                      borderLeft:
                        file.validationResult?.isValid === false
                          ? `3px solid ${theme.colors.validationWarningBorder}`
                          : "none",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        gap: "10px",
                      }}
                    >
                      <select
                        value={file.transformTo || ""}
                        onChange={(e) =>
                          onTransformToChange(
                            file.id,
                            e.target.value ? (e.target.value as FileType) : null
                          )
                        }
                        disabled={isUploading || isDetecting}
                        style={{
                          padding: "6px 32px 6px 10px",
                          border: `1px solid ${theme.colors.dropdownBorderColor}`,
                          borderRadius: "6px",
                          fontSize: "13px",
                          background: theme.colors.backgroundTertiary,
                          color: theme.colors.text,
                          cursor:
                            isUploading || isDetecting
                              ? "not-allowed"
                              : "pointer",
                          fontFamily: theme.fonts.primary,
                          opacity: isUploading || isDetecting ? 0.5 : 1,
                          minWidth: 0,
                        }}
                      >
                        <option hidden value="">
                          Select type...
                        </option>
                        {/* XML is always an option if detected type is PROTO */}
                        {file.detectedType === FileType.PROTO && (
                          <option value={FileType.XML}>XML</option>
                        )}
                        {/* PROTO is always an option if detected type is XML */}
                        {file.detectedType === FileType.XML && (
                          <option value={FileType.PROTO}>Protobuf</option>
                        )}
                        {/* CPP is only available for PROTO files */}
                        {file.detectedType === FileType.PROTO && (
                          <option value={FileType.CPP}>C++</option>
                        )}
                      </select>

                      {/* Always reserve space for validation icon */}
                      <div
                        style={{
                          width: "24px",
                          height: "24px",
                          flexShrink: 0,
                          flexGrow: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                        }}
                      >
                        {file.validationResult &&
                          file.validationResult.isValid === false && (
                            <QuickFormValidationIcon
                              theme={theme}
                              validationResult={file.validationResult}
                            />
                          )}
                      </div>
                    </div>
                  </td>

                  {/* Transform Status */}
                  <td
                    style={{
                      padding: theme.spacing.lg,
                      verticalAlign: "middle",
                      borderRight: `1px solid ${theme.colors.searchMainColor}`,
                      borderTop: `1px solid ${theme.colors.searchMainColor}`,
                    }}
                  >
                    <TransformStatusIcon
                      status={file.transformStatus}
                      theme={theme}
                      errorMessage={file.errorMessage}
                      onErrorClick={
                        file.errorMessage
                          ? () =>
                              onShowErrorDetails(
                                file.id,
                                file.errorMessage || ""
                              )
                          : undefined
                      }
                    />
                  </td>

                  {/* Actions */}
                  <td
                    style={{
                      padding: theme.spacing.lg,
                      paddingRight: "30px",
                      verticalAlign: "middle",
                      borderTop: `1px solid ${theme.colors.searchMainColor}`,
                    }}
                  >
                    <div style={{ display: "flex", gap: theme.spacing.sm }}>
                      <StatefulTooltip
                        placement={PLACEMENT.top}
                        content={"Download File"}
                        overrides={getTooltipOverrides(currentTheme)}
                      >
                        <button
                          data-testId="download-quickform-file-button"
                          onClick={() => onDownloadFile(file.id)}
                          disabled={
                            file.uploadStatus !== UploadStatus.COMPLETE ||
                            file.transformStatus !== "success"
                          }
                          style={{
                            width: "32px",
                            height: "32px",
                            border: "none",
                            borderRadius: "6px",
                            background: theme.colors.backgroundTertiary,
                            cursor:
                              file.uploadStatus === UploadStatus.COMPLETE &&
                              file.transformStatus === "success"
                                ? "pointer"
                                : "not-allowed",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                            fontSize: "16px",
                            opacity:
                              file.uploadStatus === UploadStatus.COMPLETE &&
                              file.transformStatus === "success"
                                ? 1
                                : 0.4,
                          }}
                        >
                          <FaDownload size={14} color={theme.colors.text} />
                        </button>
                      </StatefulTooltip>
                      <StatefulTooltip
                        placement={PLACEMENT.top}
                        content={"Remove File"}
                        overrides={getTooltipOverrides(currentTheme)}
                      >
                        <button
                          data-testId="remove-quickform-file-button"
                          onClick={() => onRemoveFile(file.id)}
                          disabled={isUploading}
                          style={{
                            width: "32px",
                            height: "32px",
                            border: "none",
                            borderRadius: "6px",
                            background: theme.colors.backgroundTertiary,
                            cursor: isUploading ? "not-allowed" : "pointer",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            transition: "all 0.2s",
                            fontSize: "16px",
                            opacity: isUploading ? 0.4 : 1,
                          }}
                          onMouseEnter={(e) => {
                            if (!isUploading) {
                              e.currentTarget.style.background = `rgba(255, 85, 85, 0.2)`;
                            }
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.background =
                              theme.colors.backgroundTertiary;
                          }}
                        >
                          <FaTrash size={14} color={theme.colors.text} />
                        </button>
                      </StatefulTooltip>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {files.length === 0 && (
          <div
            style={{
              textAlign: "center",
              padding: "60px 20px",
              color: theme.colors.secondaryTextColor,
            }}
          >
            <div
              style={{
                marginBottom: theme.spacing.lg,
              }}
            >
              <FaFolder
                size={64}
                style={{
                  color: theme.colors.secondaryTextColor,
                  opacity: 0.3,
                }}
              />
            </div>
            <h3
              style={{
                fontSize: theme.fontSizes.large,
                fontWeight: 600,
                marginBottom: theme.spacing.sm,
                color: theme.colors.text,
              }}
            >
              No files uploaded yet
            </h3>
            <p style={{ fontSize: theme.fontSizes.medium }}>
              Drop files in the zone above to get started with batch file
              transformation
            </p>
          </div>
        )}
      </div>
    </>
  );
};
